import {
  AlertTriangle,
  CalendarCheck,
  CalendarDays,
  DollarSign,
  FileText,
  FlaskConical,
  HeartPulse,
  Home,
  List,
  MoreHorizontal,
  Pill,
  Receipt,
  Scissors,
  Shield,
  User,
  Users,
} from "lucide-react";
import { PAT } from "@/constants/testIds";
import { CHART_DIVIDERS } from "@/lib/patientChartClinical";

const DIVIDER_ICONS = {
  "front-desk": Users,
  clinical: HeartPulse,
  billing: DollarSign,
};

const CLINICAL_ICONS = {
  summary: Home,
  "problem-list": List,
  "medical-history": HeartPulse,
  "surgical-history": Scissors,
  encounters: CalendarDays,
  appointments: CalendarCheck,
  notes: FileText,
  results: FlaskConical,
  medications: Pill,
  allergies: AlertTriangle,
  miscellaneous: MoreHorizontal,
};

const FRONT_DESK_ICONS = {
  demographics: User,
  insurance: Shield,
  appointments: CalendarCheck,
};

const BILLING_ICONS = {
  charges: Receipt,
  claims: FileText,
  payments: DollarSign,
};

function tabIcon(map, id) {
  return map[id] || FileText;
}

function isDarkPaper(hex) {
  const n = String(hex || "").replace("#", "");
  if (n.length < 6) return false;
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 < 140;
}

export function ChartDomainDividers({ activeId, onSelect, visibleIds }) {
  const tabs = CHART_DIVIDERS.filter((item) => !visibleIds || visibleIds.includes(item.id)).map((item) => ({
    id: item.id,
    label: item.label,
    color: item.folderColor,
    subtitle: item.subtitle,
  }));
  return (
    <ChartSubtabs
      tabs={tabs}
      activeId={activeId}
      onSelect={onSelect}
      icons={DIVIDER_ICONS}
      testId={PAT.chartDomainTabs}
      tabTestIdPrefix={PAT.chartDomain}
      ariaLabel="Patient chart sections"
      listClass="chart-subtabs--clinical chart-subtabs--domain"
    />
  );
}

export function ChartSubtabs({
  tabs,
  activeId,
  onSelect,
  icons = {},
  testId,
  tabTestIdPrefix,
  ariaLabel,
  listClass = "",
}) {
  return (
    <div
      data-testid={testId}
      className={`chart-subtabs${listClass ? ` ${listClass}` : ""}`}
      role="tablist"
      aria-label={ariaLabel}
    >
      {tabs.map((item) => {
        const Icon = tabIcon(icons, item.id);
        const active = activeId === item.id;
        const dark = !active && isDarkPaper(item.color);
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active}
            data-testid={`${tabTestIdPrefix}-${item.id}`}
            onClick={() => onSelect(item.id)}
            className={`chart-subtab${active ? " is-active" : ""}${dark ? " is-dark" : ""}`}
            style={{
              "--tab-color": item.color,
              "--tab-bg": active ? "#fffdf8" : item.color,
            }}
          >
            <Icon className="chart-subtab-icon" aria-hidden="true" />
            <span className={item.subtitle ? "chart-divider-label" : undefined}>{item.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export function ClinicalSubtabs({ tabs, activeId, onSelect }) {
  return (
    <ChartSubtabs
      tabs={tabs}
      activeId={activeId}
      onSelect={onSelect}
      icons={CLINICAL_ICONS}
      testId={PAT.chartTabs}
      tabTestIdPrefix={PAT.chartTab}
      ariaLabel="Clinical chart sections"
      listClass="chart-subtabs--clinical"
    />
  );
}

export function FrontDeskSubtabs({ tabs, activeId, onSelect }) {
  return (
    <ChartSubtabs
      tabs={tabs}
      activeId={activeId}
      onSelect={onSelect}
      icons={FRONT_DESK_ICONS}
      testId={PAT.chartFrontDeskTabs}
      tabTestIdPrefix={PAT.chartFrontDeskTab}
      ariaLabel="Front desk chart sections"
      listClass="chart-subtabs--clinical"
    />
  );
}

export function BillingSubtabs({ tabs, activeId, onSelect }) {
  return (
    <ChartSubtabs
      tabs={tabs}
      activeId={activeId}
      onSelect={onSelect}
      icons={BILLING_ICONS}
      testId={PAT.chartBillingTabs}
      tabTestIdPrefix={PAT.chartBillingTab}
      ariaLabel="Billing chart sections"
      listClass="chart-subtabs--clinical"
    />
  );
}
